import { useMutation } from "@tanstack/react-query";

import { env } from "@/config";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { clearCallbackState, useCallbackState } from "@/sdk/sessions";
import { useSessionStore } from "@/sdk/sessions/session.store";

import type { AgentActivity } from "../types";

import { useAgentStore } from "../store";
import { useActiveTask } from "./useActiveTask";
import { useAgentSdk } from "./useAgentSdk";

export function useAgentActivity() {
    const logger = useLogger({ module: "useAgentActivity" });
    const agentSdk = useAgentSdk();
    const activeTask = useActiveTask();
    const callbackState = useCallbackState();
    const currentActivity = useAgentStore((state) => state.activity);
    // Actions are stable refs - read from getState() instead of subscribing.
    const { setPendingActivity } = useAgentStore.getState();

    function isAgentBusyWithTask() {
        if (!currentActivity) throw new Error("agent activity not yet initialized");
        switch (env.channel) {
            case "voice": {
                const taskIsBusy = !!activeTask?.isAgentBusy(currentActivity);
                const hasCallback = callbackState && callbackState.callbackDelay && callbackState.scheduledFor;
                return taskIsBusy || hasCallback;
            }
            case "chat": {
                const sessions = useSessionStore.getState().sessions;
                return sessions.some(({ tasks }) => tasks.values().some((t) => t.isAgentBusy(currentActivity)));
            }
        }
    }

    const { isPending: loadingSetAgentActivity, mutateAsync: setNewAgentActivity } = useMutation({
        mutationFn: async function setNewAgentActivity(activity: AgentActivity) {
            if (!currentActivity) throw new Error("agent activity not yet initialized");
            if (
                isAgentBusyWithTask() ||
                (!agentSdk.isAgentActivitySelectable(currentActivity) &&
                    currentActivity !== agentSdk.defaultAgentActivity)
            ) {
                if (env.channel === "chat") {
                    await agentSdk.updateAgentAttributes({
                        isPendingInactiveStatus: true,
                        pendingInactiveStatus: activity,
                    });
                }
                if (agentSdk.serviceProvider === "connect") {
                    logger.debug({ currentActivity, activity }, "useAgentActivity setting agent activity with Connect");
                    if (activity !== "Callback Pending") {
                        clearCallbackState();
                    }
                    await agentSdk.setAgentActivity(activity);
                } else {
                    logger.debug({ currentActivity, activity }, "useAgentActivity setting pending activity");
                    setPendingActivity(activity);
                }
            } else {
                logger.debug({ currentActivity, activity }, "useAgentActivity setting new agent activity");
                await agentSdk.setAgentActivity(activity);
            }
        },
    });

    return {
        loadingSetAgentActivity,
        setNewAgentActivity,
    };
}
